import type { HandleMeta, HandlePositionSide } from '../handle.schema';
import { NODE_VISUAL_CONSTANTS } from './node-defaults';

/** Canvas coordinates, shared by cards, ports and the connection hit targets. */
export const NODE_LAYOUT = {
  headerHeight: NODE_VISUAL_CONSTANTS.title.height,
  handleSize: 24,
  handleGap: 32,
  edgeInset: 20,
  minWidth: 240,
} as const;

export interface NodeHandlePlacement {
  handle: HandleMeta;
  side: HandlePositionSide;
  offset: number;
}

/**
 * Ports occupy fixed rows below the title, independent of preview/content height.
 * Explicit ordering wins; saved percentage positions only determine relative
 * order within the remaining ports. IDs and connection semantics stay intact.
 * The card grows to fit ports instead of clamping several onto the same point.
 */
export function getNodeHandleLayout(handles: readonly HandleMeta[]) {
  const sides: HandlePositionSide[] = ['left', 'right', 'top', 'bottom'];
  const placements: NodeHandlePlacement[] = [];
  let minHeight = 0;
  let minWidth = 0;

  for (const side of sides) {
    const horizontal = side === 'top' || side === 'bottom';
    const group = handles.filter((handle) => {
      const position = handle.position;
      return (typeof position === 'object' ? position.side : position)
        === side || (!position && (handle.type === 'input' ? 'left' : 'right') === side);
    });
    const order = (handle: HandleMeta) => {
      const position = handle.position;
      return typeof position === 'object' ? position.ordered : undefined;
    };
    const offset = (handle: HandleMeta) => {
      const position = handle.position;
      return typeof position === 'object'
        ? (horizontal ? position.offsetX : position.offsetY) ?? 0.5
        : 0.5;
    };
    group.sort((a, b) => {
      const aOrder = order(a);
      const bOrder = order(b);
      if (aOrder !== undefined || bOrder !== undefined) {
        return (aOrder ?? Infinity) - (bOrder ?? Infinity);
      }
      return offset(a) - offset(b);
    });

    const start = NODE_LAYOUT.edgeInset + (horizontal ? 0 : NODE_LAYOUT.headerHeight);
    group.forEach((handle, index) => {
      placements.push({ handle, side, offset: start + index * NODE_LAYOUT.handleGap });
    });
    if (group.length) {
      const extent = start + (group.length - 1) * NODE_LAYOUT.handleGap + NODE_LAYOUT.edgeInset;
      if (horizontal) minWidth = Math.max(minWidth, extent);
      else minHeight = Math.max(minHeight, extent);
    }
  }
  return { placements, minWidth, minHeight };
}
